import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Client, ClientName, RestrictedClient } from '../../../api/app-types/client.app';
import { DashboardItem } from '../../../api/app-types/dashboard.app';
import { CLIENT_API } from '../../../api/injection-tokens/services.token';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { STATE } from '../../../core/services/state.service';
import { isRestrictedClient } from '../../helpers/client-type.func';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { LocalStorageService } from '../../storage/local-storage.service';

export type ClientResolved = { kind: 'full'; value: Client } | { kind: 'restricted'; value: RestrictedClient };

const resolveInitialPageSize = (
	storage: LocalStorageService,
	auth: MsalService | null,
	preferenceKeys: string[],
	defaultPageSize: number,
	pageSizeOptions: number[],
) => {
	const userId = storage.resolveCurrentUserId(auth);
	if (!userId) return defaultPageSize;

	for (const preferenceKey of preferenceKeys) {
		const saved = storage.getPageSize(userId, preferenceKey);
		if (saved && pageSizeOptions.includes(saved)) {
			return saved;
		}
	}

	return defaultPageSize;
};

export const clientResolver: ResolveFn<ClientResolved> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const state = inject(STATE);
	const clientId = route.paramMap.get('id');
	const id = Number.parseInt(clientId!);

	// State owns the actual client/assignments/contacts fetch (with its already-loaded short
	// circuit and in-flight deduplication). The resolver only needs to await it so that
	// navigation keeps blocking until the client is available, as it did before.
	await state.setCurrentClient(id);

	const client = state.currentClient();

	if (!client) {
		// Fail explicitly instead of returning undefined, so catchErrorOfResolve's existing
		// navigation-error handling (logging + errSrv.handleError) still runs for this case.
		throw new Error(`Client ${id} could not be loaded`);
	}

	return isRestrictedClient(client) ? { kind: 'restricted', value: client } : { kind: 'full', value: client };
};

export const dashboardItemsResolver: ResolveFn<PaginatedResponse<DashboardItem>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CLIENT_API);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });
	const representationMode = inject(REPRESENTATION_MODE);
	const initialRange = resolveInitialPageSize(
		storage,
		auth,
		['client-list', 'specialists-overview'],
		env.defaultPageSize,
		env.pageSizeOptions,
	);
	const userId = representationMode.isActive() ? representationMode.mode().representedUserId : undefined;
	const response = await api.getAllDashboard(env.pagination.offset, initialRange, userId);
	return response;
};

export const clientsResolver: ResolveFn<PaginatedResponse<Client>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CLIENT_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAll(env.pagination.offset, env.pagination.range);
	return response;
};

export const clientNameResolver: ResolveFn<ClientName | undefined> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CLIENT_API);
	const clientId = route.paramMap.get('id');
	if (!clientId) {
		console.warn('No client ID provided');
		return undefined;
	}
	const client = await api.getNameOnly(Number.parseInt(clientId));
	if (!client) {
		console.warn('Client not found');
		return undefined;
	}
	return client;
};
